import { VariantDto } from '@flagsync/skyline';
import { LucideIcon } from 'lucide-react';
import * as React from 'react';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
// Light build: the default export bundles every highlight.js language
// (~900 KB minified); VariantValue only ever renders JSON.
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/light';
import style from 'react-syntax-highlighter/dist/esm/styles/hljs/github';

import { Skeleton } from '@/components/ui/skeleton';
import { TypographyInlineCode } from '@/components/ui/typography';

import { cn } from '@/lib/utils';
import { STREAM_LOADING } from '@/pages/authenticated/dashboard/ai/utils/dto-mapper';
import { VariantSumRow } from '@/pages/authenticated/dashboard/flags/detail/insights/components/impressions-table/types';
import { DataTypeCode, DataTypeCodeUnion } from '@/services/types';
import { getDeterministicColorHex } from '@/utils/color-utils';
import { truncateStr } from '@/utils/dropdown-utils';
import { getDeterministicShape } from '@/utils/shape-utils';

SyntaxHighlighter.registerLanguage('json', json);

type DataTypeScalarMap = {
  [DataTypeCode.BOOL]: string;
  [DataTypeCode.STR]: string;
  [DataTypeCode.NUM]: number;
  [DataTypeCode.JSON]: object;
};

type InferDataType<T> = T extends DataTypeCode ? DataTypeScalarMap[T] : never;

export function deriveDataTypeCodeFromValue(value: any): DataTypeCode {
  if (typeof value === 'boolean') {
    return DataTypeCode.BOOL;
  }
  if (typeof value === 'number') {
    return DataTypeCode.NUM;
  }
  if (typeof value === 'string') {
    return DataTypeCode.STR;
  }
  return DataTypeCode.JSON;
}

export function deriveValueFromDataTypeCode<T extends DataTypeCodeUnion>(
  dataTypeCode: T,
  variant: VariantDto,
): InferDataType<T> {
  switch (dataTypeCode) {
    case DataTypeCode.BOOL:
      return (variant.booleanValue ? 'true' : 'false') as InferDataType<T>;
    case DataTypeCode.NUM:
      return variant.numberValue as InferDataType<T>;
    case DataTypeCode.STR:
      return variant.stringValue as InferDataType<T>;
    case DataTypeCode.JSON:
      return variant.jsonValue as InferDataType<T>;
  }
  return undefined as never;
}

export const VariantValue = ({
  variant,
  className,
  index,
}: {
  variant?: VariantDto;
  className?: string;
  index?: number;
}) => {
  if (!variant) return 'undefined';

  const value = deriveValueFromDataTypeCode(variant.dataTypeCode, variant);

  let hexColor;
  if (index !== undefined) {
    hexColor = getDeterministicColorHex(index);
  }

  if (variant.dataTypeCode === DataTypeCode.BOOL) {
    return (
      <TypographyInlineCode className={className}>{value}</TypographyInlineCode>
    );
  }

  if (variant.dataTypeCode === DataTypeCode.JSON) {
    return (
      <SyntaxHighlighter
        customStyle={{
          backgroundColor: '#cbd5e1',
          borderRadius: '1em',
          padding: '1em',
        }}
        language="json"
        style={{ ...style }}
      >
        {JSON.stringify(value, null, 2)}
      </SyntaxHighlighter>
    );
  }

  return (
    <span className={cn('inline-flex items-center space-x-2', className)}>
      {index !== undefined && (
        <div
          className="h-3 w-3 rounded"
          style={{ backgroundColor: hexColor }}
        />
      )}
      <div className={cn('max-w-[300px] truncate', className)}>{value}</div>
    </span>
  );
};

export const VariantName = ({
  variant,
  className,
  iconClassName,
  index,
}: {
  // Only `name` is read, so anything carrying a name works (e.g. a create-flag
  // FormVariant), alongside the richer DTO shapes.
  variant?: VariantDto | VariantSumRow | { name: string };
  className?: string;
  iconClassName?: string;
  index?: number;
}) => {
  if (!variant) return 'undefined';

  const name = variant.name;

  let hexColor;
  let ShapeIcon: LucideIcon | undefined;
  if (index !== undefined) {
    ShapeIcon = getDeterministicShape(index);
    hexColor = getDeterministicColorHex(index);
  }

  if (name === STREAM_LOADING) {
    return <Skeleton className="h-4 w-16" />;
  }

  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      {ShapeIcon !== undefined && (
        <ShapeIcon
          className={cn('h-4', iconClassName)}
          style={{ color: hexColor }}
          fill={hexColor}
        />
      )}
      <div>{truncateStr(name)}</div>
    </div>
  );
};

export function VariantShape({
  index,
  className,
}: {
  index: number;
  className?: string;
}) {
  const hexColor = getDeterministicColorHex(index);
  const ShapeIcon = getDeterministicShape(index);
  return (
    <ShapeIcon
      className={cn('h-4', className)}
      style={{ color: hexColor }}
      fill={hexColor}
    />
  );
}
